import axios from "axios";
import jwtDecode from "jwt-decode";
import { LOCAL_STORAGE_KEY } from "./types";

class Client {
  static getJWT() {
    return sessionStorage.getItem(LOCAL_STORAGE_KEY);
  }

  // baseURL: '/v1/',
  static runsheetsAPI() {
    const jwt = Client.getJWT();
    const apiToken = jwtDecode(jwt);
    const userApiKey = apiToken["user.key"];
    const headers = {
      Authorization: `Token token="${userApiKey}"`
    };
    return axios.create({
      baseURL: process.env.REACT_APP_RUNSHEETS_API,
      headers
    });
  }

  // baseURL: '/v1/',
  static entitiesAPI() {
    const jwt = Client.getJWT();
    const apiToken = jwtDecode(jwt);
    const userApiKey = apiToken["user.key"];
    const headers = {
      Authorization: `Token token="${userApiKey}"`
    };
    return axios.create({
      baseURL: process.env.REACT_APP_ENTITIES_API,
      headers
    });
  }

  static testAPI() {
    const jwt = Client.getJWT();
    const apiToken = jwtDecode(jwt);
    const userApiKey = apiToken["user.key"];
    const headers = {
      Authorization: `Token token="${userApiKey}"`
    };
    return axios.create({
      baseURL: "http://vm-tst-ngapp5.nw.local:8073/v1/",
      headers
    });
  }

  static getAuth(options) {
    const userId = options.user_id;
    const { nonce, timestamp, hmac } = options;
    // Prevent any '+' or similar charaters causing encoding errors
    const encodedHmac = encodeURIComponent(hmac);
    const url = `${
      process.env.REACT_APP_ENTITIES_API
    }authsso?user_id=${userId}&nonce=${nonce}&timestamp=${timestamp}&hmac=${encodedHmac}`;
    return axios.get(url).then(response => {
      const jwt = response.data;
      Client.setToken(response.data);
      return jwt;
    });
  }

  static setToken(token) {
    this.token = token;
    // Remove old localStorage token.
    localStorage.removeItem(LOCAL_STORAGE_KEY);
    sessionStorage.setItem(LOCAL_STORAGE_KEY, token);
  }

  // RUNSHEETS
  // Need some meta data on the number of records and pages for the pagination UI
  static getRunsheets(options) {
    const {
      statuses,
      page,
      pageSize,
      sort,
      direction,
      fromDate,
      toDate,
      vehicleId,
      driverId
    } = options;
    const url = `runsheets?mode=TM&embed=**&pruning=ALL&statuses=${statuses}&scheduledFromDate=${fromDate}&scheduledToDate=${toDate}&vehicleId=${vehicleId}&userId=${driverId}&page=${page}&pageSize=${pageSize}&sort=${sort},${direction}`;
    return Client.runsheetsAPI()
      .get(url)
      .then(response => ({ runsheets: response.data, meta: response.headers }));
  }

  static getRunsheetsInDateRange(options) {
    const { fromDate, toDate, driverId, vehicleId, statuses } = options;
    const url = `runsheets?mode=TM&embed=**&pruning=ALL&statuses=${statuses}&scheduledFromDate=${fromDate}&scheduledToDate=${toDate}&vehicleId=${vehicleId}&userId=${driverId}`;
    return Client.runsheetsAPI()
      .get(url)
      .then(response => response.data);
  }

  // Duplicate runsheet function. Calls getRunsheets then modifies the data
  // returned (external ids). Probably can be removed now, logic is in
  // component.
  static duplicateRunsheet(runsheetId) {
    return Client.runsheetsAPI()
      .get(`runsheets/${runsheetId}?mode=TM&embed=**`)
      .then(response => response.data);
  }

  static getRunsheet(runsheetId) {
    return Client.runsheetsAPI()
      .get(`runsheets/${runsheetId}?mode=TM&embed=**&pruning=ALL`)
      .then(response => response.data);
  }

  static createRunsheet(attributes) {
    return Client.runsheetsAPI()
      .post("runsheets/import", attributes)
      .then(response => response.data);
  }

  static updateRunsheet(runsheetId, attributes) {
    return Client.runsheetsAPI()
      .put(`runsheets/${runsheetId}`, attributes)
      .then(response => response.data);
  }

  static cancelRunsheet(runsheetId) {
    return Client.runsheetsAPI().delete(`runsheets/${runsheetId}`);
  }

  // This needs to be reviewed...
  static uploadRunsheet(attributes) {
    return Client.runsheetsAPI().post("runsheets/upload", attributes, {
      headers: { "Content-Type": "multipart/form-data" }
    });
  }

  // JOBS
  static removeJob(runsheetId, jobId) {
    return Client.entitiesAPI().post(
      `runsheets/${runsheetId}/jobs/${jobId}/remove`
    );
  }

  // USERS
  static getUsers() {
    return Client.entitiesAPI()
      .get("users")
      .then(response => response.data);
  }

  // VEHICLES
  static getVehicles() {
    return Client.entitiesAPI()
      .get("vehicles")
      .then(response => response.data);
  }

  static getFleets() {
    return Client.entitiesAPI()
      .get("fleets")
      .then(response => response.data);
  }

  static getVehiclesInFleet(fleetId) {
    return Client.entitiesAPI()
      .get(`vehicles?fleet_id=${fleetId}`)
      .then(response => response.data);
  }

  static getVehicle(vehicleId) {
    return Client.entitiesAPI()
      .get(`vehicles/${vehicleId}`)
      .then(response => response.data);
  }

  static getLocations() {
    const types = ["BRANCH", "CUSTOMER", "DEPOT", "KIOSK", "SUPPLIER"];
    const typesParams = types.map(type => `type[]=${type}`).join("&");
    return Client.entitiesAPI()
      .get(`locations?${typesParams}`)
      .then(response => response.data);
  }

  static getLocationsByType(type) {
    return Client.runsheetsAPI()
      .get(`locations?type[]=${type}&embed=**`)
      .then(response => response.data);
  }

  static getLocationContacts(locationId) {
    return Client.entitiesAPI()
      .get(`locations/${locationId}/contacts`)
      .then(response => response.data)
      .catch(error => error)
  }

  static createLocationContact(locationId, attributes) {
    return Client.entitiesAPI()
      .post(`locations/${locationId}/contacts`, attributes)
      .then(response => response.data)
      .catch(error => error)
  }

  // JOBS
  static getJobDetails(runsheetId, jobId) {
    return Client.runsheetsAPI()
      .get(`runsheets/${runsheetId}/jobs/${jobId}?mode=TM&embed=**&pruning=ALL`)
      .then(response => response.data);
  }

  static getJobAttachment(runsheetId, jobId) {
    return Client.runsheetsAPI()
      .get(`runsheets/${runsheetId}/jobs/${jobId}/attach`)
      .then(response => response.data);
  }

  // JOB ITEMS
  static deleteJobItem(runsheetId, jobId, itemId) {
    return Client.runsheetsAPI().delete(
      `/runsheets/${runsheetId}/jobs/${jobId}/items/${itemId}`
    );
  }

  // Probably needs a better name...
  static getExternalEntities() {
    return axios
      .all([Client.getVehicles(), Client.getUsers(), Client.getLocations()])
      .then(
        axios.spread((vehicles, users, locations) => ({
          vehicles,
          users,
          locations
        }))
      );
  }

  static getUserName() {
    const jwt = Client.getJWT();
    const apiToken = jwtDecode(jwt);
    return apiToken["user.name"];
  }

  static getItemTypes() {
    return Client.entitiesAPI().get(
      '/runsheets/codes?type=item.type'
    ).then(response => response.data);
  }
}

export default Client;



// WEBPACK FOOTER //
// ./src/Client.js